const DEV_DOMAIN_STORAGE_KEY = 'devTenantDomain';

/**
 * One rule, reused for local dev, admin preview, and production alike: an explicit ?domain=
 * override wins, otherwise fall back to the real hostname. A developer on tenant-a.localhost
 * gets this for free (the OS/browser already resolve *.localhost to loopback) - no separate
 * dev-only resolution path.
 *
 * The one exception is loopback, where the hostname carries no tenant and every refresh would
 * otherwise drop back to the default theme. There the last explicit ?domain= is remembered.
 * Deliberately not done for real hostnames: the same ?domain= param drives the admin's
 * preview-by-domain, so making it sticky anywhere else would leave an admin's own portal wearing
 * the last tenant they previewed.
 */
export function resolveTenantDomain(
  search: string,
  hostname: string,
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeSessionStorage(),
): string {
  const override = new URLSearchParams(search).get('domain');
  const isLoopback = hostname === 'localhost' || hostname === '127.0.0.1';

  if (override) {
    if (isLoopback) storage?.setItem(DEV_DOMAIN_STORAGE_KEY, override);
    return override;
  }

  return (isLoopback ? storage?.getItem(DEV_DOMAIN_STORAGE_KEY) : null) || hostname;
}

// sessionStorage so it dies with the tab rather than pinning a dev to one tenant indefinitely.
// Access is guarded because it throws outright in some privacy modes, and this runs during
// APP_INITIALIZER where a throw would block bootstrap.
function safeSessionStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return sessionStorage;
  } catch {
    return null;
  }
}
